<template>
    <div>
        <div class="mb-[27px] mt-[42px] flex h-[42px] justify-between">
            <h1
                class="text-[21px] font-medium leading-normal tracking-small text-gray-800"
            >
                <span class="flex flex-wrap items-center gap-2">
                    <span class="flex items-center gap-2">
                        <span
                            class="h-2 w-2 rounded-full"
                            :class="
                                reportedDomain.active
                                    ? 'bg-green-500'
                                    : 'bg-gray-400'
                            "
                        ></span>
                        <span class="text-gray-800">
                            {{ reportedDomain.domain }}
                        </span>
                    </span>
                </span>
            </h1>
        </div>

        <BasicTabs :tabs="tabs" :tab-key="'domain-settings'" />

        <div class="mt-[42px] grid grid-cols-[260px_1fr] gap-12 mb-[150px]">
            <div class="flex flex-col">
                <div class="mb-22">
                    <h3 class="text-lg font-semibold text-gray-800">
                        {{ $t("vue.domain_status") }}
                    </h3>
                    <p class="text-sm text-gray-500">
                        {{ $t("vue.basic_info_about_your_domain") }}
                    </p>
                </div>

                <div class="mb-45">
                    <h3 class="text-lg font-semibold text-gray-800">
                        {{ $t("vue.domain_info") }}
                    </h3>
                    <p class="text-sm text-gray-500">
                        {{ $t("vue.domain_information") }}
                    </p>
                </div>

                <div class="mb-40">
                    <h3 class="text-lg font-semibold text-gray-800">
                        {{ $t("vue.domain_parking") }}
                    </h3>
                    <p class="text-sm text-gray-500">
                        {{ $t("vue.you_can_park_and_unpark_your_domain") }}
                    </p>
                </div>

                <div>
                    <h3 class="text-lg font-semibold text-gray-800">
                        {{ $t("vue.remove_domain") }}
                    </h3>
                    <p class="text-sm text-gray-500">
                        {{ $t("vue.you_can_delete_domain_and_all_reports") }}
                    </p>
                </div>
            </div>

            <div class="space-y-6">
                <div
                    class="rounded-[12.8px] border border-gray-200 bg-white p-6"
                >
                    <div class="grid grid-cols-1 gap-6 md:grid-cols-3">
                        <div>
                            <div class="text-sm font-semibold text-gray-800">
                                {{ $t("vue.domain_nominative") }}
                            </div>
                            <div class="mt-2 text-sm text-gray-500">
                                {{ reportedDomain.domain }}
                            </div>
                        </div>

                        <div>
                            <div class="text-sm font-semibold text-gray-800">
                                {{ $t("vue.status") }}
                            </div>
                            <div
                                class="policy-badge mt-2"
                                :class="
                                    reportedDomain.parked
                                        ? 'badge-parked'
                                        : reportedDomain.active
                                          ? 'badge-active'
                                          : 'badge-inactive'
                                "
                            >
                                {{
                                    reportedDomain.parked
                                        ? $t("vue.parked")
                                        : reportedDomain.active
                                          ? $t("vue.active")
                                          : $t("vue.inactive")
                                }}
                            </div>
                        </div>

                        <div>
                            <div class="text-sm font-semibold text-gray-800">
                                {{ $t("vue.created") }}
                            </div>
                            <div class="mt-2 text-sm text-gray-500">
                                {{ createdAtFormatted }}
                            </div>
                        </div>
                    </div>
                </div>

                <div
                    class="rounded-[12.8px] border border-gray-200 bg-white p-6"
                >
                    <div class="space-y-6">
                        <div>
                            <div class="text-sm font-semibold text-gray-800">
                                {{ $t("vue.domain_usage_purpose") }}
                            </div>
                            <div class="mt-2 w-[270px]">
                                <SelectInput
                                    v-model="selectedDomainUsagePurpose"
                                    :data="domainUsagePurposeOptions"
                                    :placeholder="t('vue.not_specified')"
                                    :disabled="usagePurposeSaveInProgress"
                                    size="sm"
                                />
                            </div>
                        </div>

                        <div>
                            <div class="text-sm font-semibold text-gray-800">
                                {{ $t("vue.dns_provider") }}
                            </div>
                            <div
                                class="mt-2 flex h-[35px] items-center rounded-[7px] border border-[#DDE0E5] bg-white px-[11px] py-[7px] text-[10.5px] leading-[140%] text-gray-600 w-[270px]"
                            >
                                {{ dnsProviderLabel }}
                            </div>
                        </div>
                    </div>
                </div>

                <div
                    class="mx-auto w-full rounded-md bg-white shadow-card lg:mx-0"
                >
                    <div class="p-6">
                        <h3 class="mb-1 font-semibold text-gray-800">
                            {{ $t("vue.your_domain") }}
                        </h3>
                        <p class="text-sm text-gray-500">
                            {{ reportedDomain.domain }}
                            <span class="italic">
                                ({{ $t("vue.domain_park_step_desc") }})
                            </span>
                        </p>
                    </div>

                    <div class="flex justify-end border-t border-gray-200">
                        <div class="my-3 pr-6">
                            <AtomicButton
                                type="button"
                                color="gold"
                                size="sm"
                                @click="handleParkDomainToggle"
                            >
                                {{
                                    reportedDomain.parked
                                        ? $t("vue.unpark_domain")
                                        : $t("vue.park_domain")
                                }}
                            </AtomicButton>

                            <!--                            <AtomicButton-->
                            <!--                                type="button"-->
                            <!--                                size="sm"-->
                            <!--                                @click="handleParkDomainToggle"-->
                            <!--                            >-->
                            <!--                                {{-->
                            <!--                                    reportedDomain.parked-->
                            <!--                                        ? $t("vue.unpark_domain")-->
                            <!--                                        : $t("vue.park_domain")-->
                            <!--                                }}-->
                            <!--                            </AtomicButton>-->
                        </div>
                    </div>
                </div>

                <div
                    class="mx-auto w-full rounded-md bg-white shadow-card lg:mx-0"
                >
                    <div class="p-6">
                        <h3 class="mb-1 font-semibold text-gray-800">
                            {{ $t("vue.your_domain") }}
                        </h3>
                        <p class="text-sm text-gray-500">
                            {{ reportedDomain.domain }}
                            <span class="italic">
                                ({{ $t("vue.this_step_cannot_be_undone") }})
                            </span>
                        </p>
                    </div>

                    <div class="flex justify-end border-t border-gray-200">
                        <div class="my-3 pr-6">
                            <AtomicButton
                                type="button"
                                color="red"
                                size="sm"
                                @click="deleteDomain"
                            >
                                {{ $t("vue.delete_domain") }}
                            </AtomicButton>
                            <!--                            <AtomicButton-->
                            <!--                                type="button"-->
                            <!--                                size="sm"-->
                            <!--                                @click="deleteDomain"-->
                            <!--                            >-->
                            <!--                                {{ $t("vue.delete_domain") }}-->
                            <!--                            </AtomicButton>-->
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <DomainLimitModal
            :show="showDomainLimitModal"
            :message="domainLimitMessage"
            @close="closeDomainLimitModal"
        />
        <DomainUnparkConfirmPopup
            v-model="showUnparkConfirmPopup"
            mode="domain-unpark"
            :unparked-domains-count="unparkedDomainsCount"
            @confirm="confirmUnparkDomain"
        />
    </div>
</template>

<script setup lang="ts">
import axios from "axios";
import route from "ziggy-js";
import { router } from "@inertiajs/vue3";
import { computed, ref, watch, type PropType } from "vue";
import BasicTabs from "@/components/Atomic/Tabs/BasicTabs.vue";
import AtomicButton from "@/components/Atomic/Buttons/AtomicButton.vue";
import SelectInput from "@/components/Atomic/Inputs/SelectInput.vue";
import { useDomainParking } from "@/composables/useDomainParking";
import { create } from "@/composables/useNotifications";
import { useTranslations } from "@/composables/useTranslations";
import useConfirm from "@/composables/useConfirm";
import { buildDomainReportTabs } from "@/Pages/Reports/utils/buildDomainReportTabs";
import DomainLimitModal from "@/Pages/Domains/components/DomainLimitModal.vue";
import DomainUnparkConfirmPopup from "../../components/Atomic/Dialogs/Popups/DomainUnparkConfirmPopup.vue";

const { t } = useTranslations();
const { show } = useConfirm();
const {
    parkDomainToggleWithBasicLimitGuard,
    showDomainLimitModal,
    domainLimitMessage,
    closeDomainLimitModal,
} = useDomainParking();

interface ReportedDomain {
    domain: string;
    active: boolean;
    created_at?: string;
    parked?: boolean;
}

interface DomainInfo {
    dnsProvider?: string | null;
    domainUsagePurpose?: string | null;
    domainUsagePurposeOptions?: DomainUsagePurposeOption[];
}

interface DomainUsagePurposeOption {
    labelKey: string;
    value: string;
}

const props = defineProps({
    reportedDomain: {
        type: Object as PropType<ReportedDomain>,
        required: true,
    },
    unparkedDomainsCount: {
        type: Number,
        required: false,
        default: 0,
    },
    domainInfo: {
        type: Object as PropType<DomainInfo>,
        required: false,
        default: () => ({}),
    },
});

const tabs = computed(() =>
    buildDomainReportTabs(
        props.reportedDomain.domain,
        props.reportedDomain?.parked ?? false,
        t,
    ),
);

const createdAtFormatted = computed(() => {
    if (!props.reportedDomain.created_at) {
        return "-";
    }

    return new Date(props.reportedDomain.created_at).toLocaleString();
});

const selectedDomainUsagePurpose = ref(
    normalizeOptionalValue(props.domainInfo?.domainUsagePurpose),
);
const showUnparkConfirmPopup = ref(false);
const usagePurposeSaveInProgress = ref(false);
const lastSavedDomainUsagePurpose = ref(
    normalizeOptionalValue(props.domainInfo?.domainUsagePurpose),
);

watch(
    () => props.domainInfo?.domainUsagePurpose,
    (value) => {
        const normalized = normalizeOptionalValue(value);
        selectedDomainUsagePurpose.value = normalized;
        lastSavedDomainUsagePurpose.value = normalized;
    },
);

watch(selectedDomainUsagePurpose, async (value) => {
    if (value === lastSavedDomainUsagePurpose.value) {
        return;
    }

    if (usagePurposeSaveInProgress.value) {
        return;
    }

    usagePurposeSaveInProgress.value = true;

    try {
        await axios.patch(
            route("domains.usage-purpose.update", {
                domain: props.reportedDomain.domain,
            }),
            {
                domainUsagePurpose: value || null,
            },
            {
                headers: {
                    Accept: "application/json",
                },
            },
        );

        lastSavedDomainUsagePurpose.value = value;
        create({
            title: t("vue.domain_usage_purpose_updated"),
            type: "success",
        });
    } catch (error) {
        selectedDomainUsagePurpose.value = lastSavedDomainUsagePurpose.value;
        create({
            title: t("vue.domain_usage_purpose_not_updated"),
            type: "error",
        });
    } finally {
        usagePurposeSaveInProgress.value = false;
    }
});

const domainUsagePurposeOptions = computed(() =>
    (props.domainInfo?.domainUsagePurposeOptions ?? []).map((option) => ({
        ...option,
        title: t(option.labelKey),
    })),
);

const dnsProviderLabel = computed(() =>
    normalizeDnsProviderValue(props.domainInfo?.dnsProvider),
);

const deleteDomain = async () => {
    const confirmed = await show({
        header: t("vue.delete_domain"),
        message: t("vue.are_you_sure_delete_domain"),
        type: "danger",
        confirm: t("vue.delete"),
    });

    if (confirmed !== true) {
        return;
    }

    router.delete(
        route("domains.destroy", { domain: props.reportedDomain.domain }),
        {
            onSuccess: () => {
                create({
                    title: t("vue.domain_removed"),
                    text: t("vue.domain_removed_successfully"),
                    type: "success",
                });
                router.visit(route("domains.index"));
            },
            onError: (e) => {
                create({
                    title: t("vue.error"),
                    text: e.message || t("vue.error_removing_domain"),
                    type: "error",
                });
            },
        },
    );
};

const handleParkDomainToggle = async () => {
    if (props.reportedDomain.parked === true) {
        showUnparkConfirmPopup.value = true;
        return;
    }

    const confirmed = await show({
        header: t("vue.park_domain"),
        message: t("vue.are_you_sure_park_domain"),
        type: "danger",
        confirm: t("vue.park_domain"),
    });

    if (confirmed !== true) {
        return;
    }

    parkDomainToggleWithBasicLimitGuard(props.reportedDomain, {
        unparkedDomainsCount: props.unparkedDomainsCount,
    });
};

const confirmUnparkDomain = () => {
    parkDomainToggleWithBasicLimitGuard(props.reportedDomain, {
        unparkedDomainsCount: props.unparkedDomainsCount,
    });
};

function normalizeDnsProviderValue(value?: string | null): string {
    if (!value) {
        return t("vue.not_detected");
    }

    const normalized = value.trim();

    if (normalized === "") {
        return t("vue.not_detected");
    }

    return normalized;
}

function normalizeOptionalValue(value?: string | null): string {
    if (value == null) {
        return "";
    }

    const normalized = value.trim();

    if (normalized === "" || normalized.toLowerCase() === "null") {
        return "";
    }

    return normalized;
}
</script>
